import React from 'react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import {
  EllipsisHorizontalIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { getActiveNavigationItem, getMobileNavigation, getPageNavigation, NavigationItem, SECTION_LABELS } from './navigation';
import { getNavigationIcon } from './navigationIcons';

interface MobileNavProps {
  items: NavigationItem[];
  pathname: string;
  moreOpen: boolean;
  onMoreOpenChange: (open: boolean) => void;
}

const MobileNav: React.FC<MobileNavProps> = ({ items, pathname, moreOpen, onMoreOpenChange }) => {
  const pageItems = getPageNavigation(items);
  const primaryItems = getMobileNavigation(pageItems);
  const primaryIds = new Set(primaryItems.map((item) => item.id));
  const remainingItems = pageItems.filter((item) => !primaryIds.has(item.id));
  const activeItem = getActiveNavigationItem(pageItems, pathname);
  const remainingActive = Boolean(activeItem && !primaryIds.has(activeItem.id));

  return (
    <>
      <nav
        aria-label="Mobile navigation"
        className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-slate-200 bg-white/95 [padding-bottom:env(safe-area-inset-bottom)] shadow-[0_-8px_24px_rgba(15,23,42,0.12)] backdrop-blur dark:border-slate-700 dark:bg-[#071a33]/95 xl:hidden"
      >
        {primaryItems.map((item) => {
          const Icon = getNavigationIcon(item.id);
          const active = item.id === activeItem?.id;
          return (
            <Link
              key={item.id}
              to={item.href}
              aria-current={active ? 'page' : undefined}
              className={`flex min-h-16 flex-col items-center justify-center gap-1 border-t-4 px-1 text-[0.68rem] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500 ${
                active
                  ? 'border-amber-500 text-amber-800 dark:text-amber-300'
                  : 'border-transparent text-slate-600 dark:text-slate-300'
              }`}
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              <span className="max-w-full truncate">{item.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          onClick={() => onMoreOpenChange(true)}
          aria-expanded={moreOpen}
          aria-label={remainingActive ? `More destinations, current page: ${activeItem?.label}` : 'More destinations'}
          className={`flex min-h-16 flex-col items-center justify-center gap-1 border-t-4 px-1 text-[0.68rem] font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500 ${remainingActive ? 'border-amber-500 text-amber-800 dark:text-amber-300' : 'border-transparent text-slate-600 dark:text-slate-300'}`}
        >
          <EllipsisHorizontalIcon className="h-5 w-5" aria-hidden="true" />
          More
        </button>
      </nav>

      <Dialog open={moreOpen} onClose={onMoreOpenChange} className="relative z-50 xl:hidden">
        <div className="fixed inset-0 bg-slate-950/65" aria-hidden="true" />
        <div className="fixed inset-0 flex items-end">
          <DialogPanel className="max-h-[80vh] w-full overflow-y-auto rounded-t-3xl bg-white pb-[env(safe-area-inset-bottom)] shadow-2xl dark:bg-[#071a33]">
            <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4 dark:border-slate-700 dark:bg-[#071a33]">
              <DialogTitle className="text-lg font-bold text-slate-950 dark:text-white">More destinations</DialogTitle>
              <button
                type="button"
                aria-label="Close more destinations"
                onClick={() => onMoreOpenChange(false)}
                className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full text-slate-600 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:text-slate-200 dark:hover:bg-white/10 dark:focus-visible:ring-club-gold-500"
              >
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 p-5">
              {remainingItems.map((item) => {
                const Icon = getNavigationIcon(item.id);
                const active = item.id === activeItem?.id;
                return (
                  <Link
                    key={item.id}
                    to={item.href}
                    aria-label={item.label}
                    aria-current={active ? 'page' : undefined}
                    onClick={() => onMoreOpenChange(false)}
                    className={`flex min-h-20 items-center gap-3 rounded-xl border p-3 font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-gold-600 dark:focus-visible:ring-club-gold-500 ${
                      active
                        ? 'border-amber-400 bg-amber-50 text-amber-900 dark:bg-amber-400/15 dark:text-amber-200'
                        : 'border-slate-200 text-slate-800 dark:border-slate-700 dark:text-slate-100'
                    }`}
                  >
                    <Icon className="h-6 w-6 shrink-0 text-amber-500" aria-hidden="true" />
                    <span>
                      {item.label}
                      <span className="block text-xs font-normal text-slate-500 dark:text-slate-400">{SECTION_LABELS[item.section]}</span>
                    </span>
                  </Link>
                );
              })}
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </>
  );
};

export default MobileNav;
